import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const RegistrationForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    companyName: '',
    role: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const roleOptions = [
    { value: 'solution-architect', label: 'Solution Architect' },
    { value: 'process-owner', label: 'Process Owner' },
    { value: 'implementation-consultant', label: 'Implementation Consultant' },
    { value: 'business-analyst', label: 'Business Analyst' },
    { value: 'project-manager', label: 'Project Manager' }
  ];

  const validatePassword = (password) => {
    const requirements = {
      length: password?.length >= 8,
      uppercase: /[A-Z]/?.test(password),
      lowercase: /[a-z]/?.test(password),
      number: /\d/?.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/?.test(password)
    };
    return requirements;
  };

  const getPasswordStrength = (password) => {
    const requirements = validatePassword(password);
    const score = Object.values(requirements)?.filter(Boolean)?.length;
    
    if (score < 2) return { level: 'weak', color: 'text-destructive', bg: 'bg-destructive' };
    if (score < 4) return { level: 'medium', color: 'text-warning', bg: 'bg-warning' };
    return { level: 'strong', color: 'text-success', bg: 'bg-success' };
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.companyName?.trim()) {
      newErrors.companyName = 'Company name is required';
    }

    if (!formData?.role) {
      newErrors.role = 'Please select your role';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else {
      const requirements = validatePassword(formData?.password);
      if (!Object.values(requirements)?.every(Boolean)) {
        newErrors.password = 'Password must meet all requirements';
      }
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData?.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock successful registration
      console.log('Registration successful:', formData);
      navigate('/app-dashboard');
    } catch (error) {
      setErrors({ submit: 'Registration failed. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = () => {
    return formData?.fullName?.trim() &&
           formData?.email?.trim() &&
           formData?.companyName?.trim() &&
           formData?.role &&
           formData?.password &&
           formData?.confirmPassword &&
           formData?.password === formData?.confirmPassword &&
           Object.values(validatePassword(formData?.password))?.every(Boolean) &&
           formData?.agreeToTerms;
  };

  const passwordRequirements = validatePassword(formData?.password);
  const passwordStrength = getPasswordStrength(formData?.password);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Full Name */}
      <Input
        label="Full Name"
        type="text"
        placeholder="Enter your full name"
        value={formData?.fullName}
        onChange={(e) => handleInputChange('fullName', e?.target?.value)}
        error={errors?.fullName}
        required
      />
      {/* Email */}
      <Input
        label="Email Address"
        type="email"
        placeholder="Enter your email address"
        value={formData?.email}
        onChange={(e) => handleInputChange('email', e?.target?.value)}
        error={errors?.email}
        required
      />
      {/* Company Name */}
      <Input
        label="Company Name"
        type="text"
        placeholder="Enter your company name"
        value={formData?.companyName}
        onChange={(e) => handleInputChange('companyName', e?.target?.value)}
        error={errors?.companyName}
        required
      />
      {/* Role Selection */}
      <Select
        label="Role"
        placeholder="Select your role"
        options={roleOptions}
        value={formData?.role}
        onChange={(value) => handleInputChange('role', value)}
        error={errors?.role}
        required
      />
      {/* Password */}
      <div className="space-y-2">
        <div className="relative">
          <Input
            label="Password"
            type={showPassword ? "text" : "password"}
            placeholder="Create a secure password"
            value={formData?.password}
            onChange={(e) => handleInputChange('password', e?.target?.value)}
            error={errors?.password}
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-8 text-muted-foreground hover:text-foreground"
          >
            <Icon name={showPassword ? "EyeOff" : "Eye"} size={20} />
          </button>
        </div>

        {/* Password Strength Indicator */}
        {formData?.password && (
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className={`h-full ${passwordStrength?.bg} transition-all duration-300`}
                  style={{ 
                    width: `${(Object.values(passwordRequirements)?.filter(Boolean)?.length / 5) * 100}%` 
                  }}
                />
              </div>
              <span className={`text-sm font-medium ${passwordStrength?.color}`}>
                {passwordStrength?.level?.charAt(0)?.toUpperCase() + passwordStrength?.level?.slice(1)}
              </span>
            </div>

            {/* Password Requirements */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs">
              <div className={`flex items-center space-x-1 ${passwordRequirements?.length ? 'text-success' : 'text-muted-foreground'}`}>
                <Icon name={passwordRequirements?.length ? "Check" : "X"} size={12} />
                <span>8+ characters</span>
              </div>
              <div className={`flex items-center space-x-1 ${passwordRequirements?.uppercase ? 'text-success' : 'text-muted-foreground'}`}>
                <Icon name={passwordRequirements?.uppercase ? "Check" : "X"} size={12} />
                <span>Uppercase letter</span>
              </div>
              <div className={`flex items-center space-x-1 ${passwordRequirements?.lowercase ? 'text-success' : 'text-muted-foreground'}`}>
                <Icon name={passwordRequirements?.lowercase ? "Check" : "X"} size={12} />
                <span>Lowercase letter</span>
              </div>
              <div className={`flex items-center space-x-1 ${passwordRequirements?.number ? 'text-success' : 'text-muted-foreground'}`}>
                <Icon name={passwordRequirements?.number ? "Check" : "X"} size={12} />
                <span>Number</span>
              </div>
              <div className={`flex items-center space-x-1 ${passwordRequirements?.special ? 'text-success' : 'text-muted-foreground'} sm:col-span-2`}>
                <Icon name={passwordRequirements?.special ? "Check" : "X"} size={12} />
                <span>Special character (!@#$%^&*)</span>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Confirm Password */}
      <div className="relative">
        <Input
          label="Confirm Password"
          type={showConfirmPassword ? "text" : "password"}
          placeholder="Confirm your password"
          value={formData?.confirmPassword}
          onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
          error={errors?.confirmPassword}
          required
        />
        <button
          type="button"
          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          className="absolute right-3 top-8 text-muted-foreground hover:text-foreground"
        >
          <Icon name={showConfirmPassword ? "EyeOff" : "Eye"} size={20} />
        </button>
      </div>
      {/* Terms Agreement */}
      <div className="space-y-2">
        <Checkbox
          label={
            <span className="text-sm">
              I agree to the{' '}
              <button
                type="button"
                onClick={() => window.open('/terms', '_blank')}
                className="text-primary hover:underline"
              >
                Terms of Service
              </button>
              {' '}and{' '}
              <button
                type="button"
                onClick={() => window.open('/privacy', '_blank')}
                className="text-primary hover:underline"
              >
                Privacy Policy
              </button>
            </span>
          }
          checked={formData?.agreeToTerms}
          onChange={(e) => handleInputChange('agreeToTerms', e?.target?.checked)}
          error={errors?.agreeToTerms}
          required
        />
      </div>
      {/* Submit Error */}
      {errors?.submit && (
        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-sm text-destructive">{errors?.submit}</p>
        </div>
      )}
      {/* Submit Button */}
      <Button
        type="submit"
        variant="default"
        size="lg"
        fullWidth
        loading={isLoading}
        disabled={!isFormValid() || isLoading}
        iconName="UserPlus"
        iconPosition="left"
      >
        {isLoading ? 'Creating Account...' : 'Create Account'}
      </Button>
    </form>
  );
};

export default RegistrationForm;